import { FC, useState } from "react";
import { Box, Typography, InputBase, Button } from "@mui/material";
import SettingTitle from "../component/SettingTitle";
import { styles } from "./stylesWithdrawalAddresses";
import EmptyWalletSvg from "@/assets/svg/EmptyWalletSvg";
import VerifiedCheckSvg from "@/assets/svg/VerifiedCheckSvg";
import TrashSvg from "@/assets/svg/TrashSvg";
import BluePlusSvg from "@/assets/svg/BluePlusSvg";
import Information from "@/assets/svg/Information";
import solanaImg from "@/assets/image/Solana.png";
// import lightningImg from "@/assets/image/BitcoinLightning.png";
import {
  useGetVerifiedAddressesQuery,
  useAddVerifiedAddressMutation,
  useDeleteVerifiedAddressMutation,
} from "@/store/api/walletApi";
import CustomToast from "@/components/UI/CustomToast/CustomToast";

interface ProviderConfig {
  id: string;
  cardName: string;
  listName: string;
  image: string;
  imageScale: number;
  placeholder: string;
}

const PROVIDERS: ProviderConfig[] = [
  {
    id: "solana",
    cardName: "Solana",
    listName: "Solana Wallet",
    image: solanaImg,
    imageScale: 1.58,
    placeholder: "Enter your Solana wallet address",
  },
  // {
  //   id: "lightning",
  //   cardName: "Bitcoin Lightning",
  //   listName: "Bitcoin Lightning",
  //   image: lightningImg,
  //   imageScale: 1.34,
  //   placeholder: "Enter your Lightning address",
  // },
];

const getProvider = (id: string) => PROVIDERS.find((p) => p.id === id) ?? PROVIDERS[0];

const ProviderIcon = ({ provider }: { provider: ProviderConfig }) => (
  <Box sx={styles.providerIconBox}>
    <Box component="img" src={provider.image} alt="" sx={styles.providerIconImg(provider.imageScale)} />
  </Box>
);

const WithdrawalAddresses: FC = () => {
  const { data: saved = [] } = useGetVerifiedAddressesQuery();
  const [addVerifiedAddress, { isLoading: isAdding }] = useAddVerifiedAddressMutation();
  const [deleteVerifiedAddress] = useDeleteVerifiedAddressMutation();

  const [view, setView] = useState<"list" | "form">("list");
  const [selectedProviderId, setSelectedProviderId] = useState<string>(PROVIDERS[0].id);
  const [address, setAddress] = useState("");
  const [toast, setToast] = useState<{ message: string; success: boolean } | null>(null);

  const allAdded = saved.length >= PROVIDERS.length;
  const isApproveActive = address.trim().length > 3 && !isAdding;

  const openForm = () => {
    const firstAvailable = PROVIDERS.find((p) => !saved.some((s) => s.network === p.id));
    setSelectedProviderId((firstAvailable ?? PROVIDERS[0]).id);
    setAddress("");
    setView("form");
  };

  const handleApprove = async () => {
    if (!isApproveActive) return;
    try {
      await addVerifiedAddress({
        network: selectedProviderId,
        address: address.trim(),
      }).unwrap();
      setAddress("");
      setView("list");
    } catch (e: any) {
      const message = e?.data?.message;
      setToast({
        message: Array.isArray(message)
          ? message[0]
          : message || "Failed to save withdrawal address",
        success: false,
      });
    }
  };

  const handleDelete = async (uid: string) => {
    try {
      await deleteVerifiedAddress(uid).unwrap();
    } catch (e: any) {
      const message = e?.data?.message;
      setToast({
        message: Array.isArray(message)
          ? message[0]
          : message || "Failed to delete withdrawal address",
        success: false,
      });
    }
  };

  const selectedProvider = getProvider(selectedProviderId);

  if (view === "form") {
    return (
      <Box sx={styles.root}>
        {toast && (
          <CustomToast
            message={toast.message}
            success={toast.success}
            onClose={() => setToast(null)}
          />
        )}
        <SettingTitle title="Withdrawal Addresses" back isActive onBack={() => setView("list")} />

        <Box sx={styles.providerSection}>
          <Typography sx={styles.formLabel}>Select Provider</Typography>
          <Box sx={styles.providerCards}>
            {PROVIDERS.map((provider) => (
              <Box
                key={provider.id}
                onClick={() => setSelectedProviderId(provider.id)}
                sx={styles.providerCard(selectedProviderId === provider.id)}
              >
                <ProviderIcon provider={provider} />
                <Typography sx={styles.providerCardName}>{provider.cardName}</Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Box sx={styles.formSection}>
          <Box sx={styles.formGroup}>
            <Box sx={styles.field}>
              <Typography sx={styles.formLabel}>Wallet Address</Typography>
              <InputBase
                placeholder={selectedProvider.placeholder}
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                sx={styles.input}
              />
            </Box>

            <Box sx={styles.notice}>
              <Box sx={styles.noticeHeadRow}>
                <Information />
                <Typography sx={styles.noticeTitle}>Verification Required</Typography>
              </Box>
              <Typography sx={styles.noticeDesc}>
                We&apos;ll send a{" "}
                <Box component="span" sx={styles.noticeStrong}>
                  $1 test withdrawal
                </Box>{" "}
                to this wallet to make sure your address is correct before you can save it. Total
                charged to your platform balance:
              </Typography>
              <Box component="ul" sx={styles.noticeList}>
                <Box component="li" sx={styles.noticeListItem}>
                  Solana:{" "}
                  <Box component="span" sx={styles.noticeStrong}>
                    $1.01
                  </Box>{" "}
                  (includes 1% network fee)
                </Box>
                {/* <Box component="li" sx={styles.noticeListItem}>
                  Bitcoin Lightning:{" "}
                  <Box component="span" sx={styles.noticeStrong}>
                    $1.02
                  </Box>{" "}
                  (includes 2% network fee)
                </Box> */}
              </Box>
              <Typography sx={styles.noticeDesc}>
                Once you approve the withdrawal, check your external wallet. If the $1 arrived
                safely, come back and confirm — we&apos;ll then verify the address and add it to
                your saved list.
              </Typography>
            </Box>
          </Box>

          <Box sx={styles.buttonsRow}>
            <Button disableRipple sx={styles.cancelBtn} onClick={() => setView("list")}>
              Cancel
            </Button>
            <Button
              variant="contained"
              disableRipple
              disabled={!isApproveActive}
              onClick={handleApprove}
              sx={styles.approveBtn(isApproveActive)}
            >
              Approve $1 &amp; Connect Wallet
            </Button>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={styles.root}>
      {toast && (
        <CustomToast
          message={toast.message}
          success={toast.success}
          onClose={() => setToast(null)}
        />
      )}
      <SettingTitle title="Withdrawal Addresses" back isActive />

      <Box sx={styles.subtitleRow}>
        <Typography sx={styles.subtitle}>
          Manage your connected wallets to withdraw funds earned on the platform.
        </Typography>
      </Box>

      <Box sx={{ ...styles.content, gap: saved.length ? "16px" : "12px" }}>
        {saved.length === 0 ? (
          <Box sx={styles.emptyBox}>
            <Box sx={styles.emptyIconCircle}>
              <EmptyWalletSvg width={24} height={24} />
            </Box>
            <Box sx={styles.emptyTextWrap}>
              <Typography sx={styles.emptyTitle}>No wallets connected</Typography>
              <Typography sx={styles.emptyDesc}>
                Add a withdrawal address to receive your earnings. Supported provider: Solana.
              </Typography>
            </Box>
          </Box>
        ) : (
          saved.map((item) => {
            const provider = getProvider(item.network);
            return (
              <Box key={item.uid} sx={styles.listItem}>
                <ProviderIcon provider={provider} />
                <Box sx={styles.itemNameCol}>
                  <Box sx={styles.itemNameRow}>
                    <Typography sx={styles.itemName}>{provider.listName}</Typography>
                    <Box sx={styles.verifiedBadge}>
                      <VerifiedCheckSvg width={14} height={14} />
                      <Typography sx={styles.verifiedText}>Verified</Typography>
                    </Box>
                  </Box>
                  <Typography sx={styles.itemAddress}>{item.address}</Typography>
                </Box>
                <Box sx={styles.deleteBtn} onClick={() => handleDelete(item.uid)}>
                  <TrashSvg width={20} height={20} />
                </Box>
              </Box>
            );
          })
        )}

        <Box sx={styles.addButton(allAdded)} onClick={allAdded ? undefined : openForm}>
          <BluePlusSvg />
          <Typography sx={styles.addButtonText}>Add Withdrawal Address</Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default WithdrawalAddresses;
